import React, { useState, useEffect } from "react";
import { Box, CircularProgress, Icon, Typography } from "@mui/material";
import WDTypography from "../../../MaterialComponents/WDTypography";
import WDBox from "../../../MaterialComponents/WDBox";
import WDButton from "../../../MaterialComponents/WDButton";
import apiService from "../../../services/api";
import pxToRem from "../../../Themes/Functions/pxToRem";
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StarIcon from '@mui/icons-material/Star';
// Centralized auth is handled via axios interceptors in apiService

// Constants for API endpoints (relative paths)
const FILE_UPLOAD_ENDPOINT = "/v1/files/upload";
const FILE_DOWNLOAD_ENDPOINT = "/v1/files/download";
const USER_DOCUMENTS_BASE = "/v1/users";



/**
 * Component for handling file uploads and document references in workflow forms
 * Supports both new file uploads and displaying existing document references
 * Uses global form data reference (window.currentFormData) when props are not provided
 */
const WorkflowFileField = ({ field, onFileUpload, onFileRemove, fieldName, formData, setFormData }) => {
  const [file, setFile] = useState();
  const [isFileUploading, setFileUploading] = useState(false);
  const [fileError, setFileError] = useState(null);
  
  // Helper: parse documentReferences which can arrive either as an array of objects
  // or as a Java Map-style string like "[{id=..., fileName=..., filePath=..., isPrimary=true}]"
  const parseDocumentReferences = (raw) => {
    try {
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string') {
        // Extract each {...} entry
        const entries = [];
        const regex = /\{([^}]*)\}/g;
        let match;
        while ((match = regex.exec(raw)) !== null) {
          const objStr = match[1]; // key=value, key2=value2
          const parts = objStr.split(/,\s+/);
          const obj = {};
          parts.forEach(p => {
            const eqIdx = p.indexOf('=');
            if (eqIdx > -1) {
              const key = p.substring(0, eqIdx).trim();
              const valueRaw = p.substring(eqIdx + 1).trim();
              // Convert booleans
              if (valueRaw === 'true') obj[key] = true;
              else if (valueRaw === 'false') obj[key] = false;
              else obj[key] = valueRaw;
            }
          });
          entries.push(obj);
        }
        return entries;
      }
      // Unknown type
      return [];
    } catch (e) {
      console.warn('Failed to parse documentReferences', e, raw);
      return [];
    }
  };
  
  // Use global form data reference if props not provided
  const currentFormData = formData || window.currentFormData || {};
  const updateFormState = setFormData || ((newData) => {
    window.currentFormData = newData;
    // This component doesn't have direct access to the parent's setFormData
    // The parent component should pick up changes from window.currentFormData
    console.log('Updated global form data reference:', newData);
  });
  
  // Get the current value from formData if available
  const fieldValue = currentFormData?.additionalInfo?.[fieldName];
  
  // Handle file selection
  const onFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  // Upload file when selected
  useEffect(() => {
    if (file) {
      uploadFile();
    }
  }, [file]);

  // File upload handler
  const uploadFile = () => {
    setFileUploading(true);
    const fileFormData = new FormData();
    fileFormData.append("file", file);
    // Ensure backend receives proper metadata so it doesn't default to PROFILE_IMAGE
    try {
      fileFormData.append('fileType', 'USER_DOCUMENT');
      if (file && file.name) {
        fileFormData.append('fileName', file.name);
      }
      // Resolve employeeUid from multiple possible sources
      const ctx = (formData || window.currentFormData || {});
      let employeeUid = ctx.employeeUid ||
                        (ctx.selectedEmployee && ctx.selectedEmployee.employeeId) ||
                        (window.selectedEmployee && window.selectedEmployee.employeeId) ||
                        (window.currentEmployeeId) ||
                        (window.currentEmployeeUid);
      if (employeeUid) {
        fileFormData.append('employeeUid', employeeUid);
      }
    } catch (e) {
      console.warn('Failed appending extra form fields for upload', e);
    }
    
    // Upload via centralized axios client
    apiService.api.post(FILE_UPLOAD_ENDPOINT, fileFormData, { headers: { 'Content-Type': 'multipart/form-data' }})
      .then((axiosResponse) => {
        const response = axiosResponse?.data ?? axiosResponse;
        console.log("File upload successful:", response);
        
        // Call the parent component's upload handler if provided
        if (onFileUpload) {
          onFileUpload(response, field, fieldName);
        } else {
          // Default behavior: update the form data directly
          updateFormDataWithFile(response);
        }
      })
      .catch((error) => {
        console.error("File upload failed:", error);
        setFile(undefined);
      })
      .finally(() => {
        setFileUploading(false);
      });
  };

  // Default handler to update form data with file info
  const updateFormDataWithFile = (fileResponse) => {
    if (!fieldName) return;
    
    // Get the most current form data (either from props or global reference)
    const latestFormData = formData || window.currentFormData || {};
    const updatedFormData = JSON.parse(JSON.stringify(latestFormData));
    
    if (!updatedFormData.additionalInfo) {
      updatedFormData.additionalInfo = {};
    }
    
    // Create file reference object
    const id = fileResponse?.id || fileResponse?.documentId || fileResponse?.data?.id || fileResponse?.data?.documentId || null;
    const path = fileResponse?.path || fileResponse?.filePath || fileResponse?.data?.path || fileResponse?.data?.filePath || null;
    updatedFormData.additionalInfo[fieldName] = {
      id,
      path,
      fileName: file?.name,
      contentType: file?.type,
      fileSize: file?.size,
      uploadDate: new Date().toISOString()
    };
    
    // Update form data (either via props or global reference)
    updateFormState(updatedFormData);
    
    // Always update global reference as a fallback
    window.currentFormData = updatedFormData;
    
    console.log(`Updated form data with file for field ${fieldName}:`, updatedFormData.additionalInfo[fieldName]);
  };

  // Render action button (delete or loading indicator)
  const renderActionButton = (hasDocument) => {
    if (isFileUploading) {
      return <CircularProgress size={16} />;
    }
    
    // Show delete button only when an actual document is present
    if (hasDocument) {
      return (
        <Icon 
          fontSize="small" 
          onClick={() => {
            if (onFileRemove) {
              onFileRemove(field, fieldName);
            } else {
              // Default behavior: clear the field in form data
              // Get the most current form data (either from props or global reference)
              const latestFormData = formData || window.currentFormData || {};
              const updatedFormData = JSON.parse(JSON.stringify(latestFormData));
              
              if (updatedFormData.additionalInfo) {
                updatedFormData.additionalInfo[fieldName] = "";
                
                // Update form data (either via props or global reference)
                updateFormState(updatedFormData);
                
                // Always update global reference as a fallback
                window.currentFormData = updatedFormData;
                
                console.log(`Removed file from field ${fieldName}`);
              }
            }
          }}
          sx={{ cursor: 'pointer', color: 'error.main' }}
        >
          delete
        </Icon>
      );
    }
    
    return null;
  };

  // Render file area (upload button or view link)
  const renderFileArea = () => {
    // Securely fetch a URL via centralized axios and open in same window via blob
    const openWithAuth = async (urlPath, name) => {
      try {
        // Clear any previous error before attempting
        setFileError(null);
        const axiosResp = await apiService.api.get(urlPath, { responseType: 'blob' });
        const blob = axiosResp.data;
        const blobUrl = URL.createObjectURL(blob);
        // Open in same window
        window.location.href = blobUrl;
      } catch (err) {
        console.error('Error opening document with auth', err);
        if (!fileError) {
          setFileError('Unable to open file');
        }
      }
    };

    // Function to handle document preview using the parent's file viewer dialog
    const handlePreviewDocument = async () => {
      // Build relative URLs; base is handled by apiService
      console.log('Preview document:', fieldValue);
      
      // If we have a document reference from employee data
      if (fieldValue && fieldValue.isReference) {
        // Construct document URL based on employee UID and document type/ID
        const documentUrl = fieldValue.documentUrl || 
                          `${USER_DOCUMENTS_BASE}/${fieldValue.employeeUid}/documents/${fieldValue.documentType}/download`;
        
        console.log('Document URL for reference:', documentUrl);
        
        // Check if parent component has exposed handleDocumentPreview function
        if (window.handleDocumentPreview) {
          // Call the parent's document preview function with document info
          window.handleDocumentPreview({
            name: fieldValue.fileName || fieldValue.documentType || "document",
            documentUrl: documentUrl
          });
        } else {
          // Securely open using Authorization header
          await openWithAuth(documentUrl, fieldValue.fileName || fieldValue.documentType || 'document');
        }
      } 
      // If we have a document in the target.value format
      else if (fieldValue && fieldValue.target && fieldValue.target.value && 
               (fieldValue.target.value.isReference || fieldValue.target.value.documentUrl)) {
        
        const targetValue = fieldValue.target.value;
        
        // Use documentUrl from the target.value if available
        const documentUrl = targetValue.documentUrl || 
                          `${USER_DOCUMENTS_BASE}/${(targetValue.uid || targetValue.employeeUid)}/documents/${targetValue.documentType}/download`;
        
        console.log('Document URL for target.value structure:', documentUrl);
        
        // Check if parent component has exposed handleDocumentPreview function
        if (window.handleDocumentPreview) {
          // Call the parent's document preview function with document info
          window.handleDocumentPreview({
            name: targetValue.fileName || targetValue.documentType || fieldValue.target.name || "document",
            documentUrl: documentUrl
          });
        } else {
          // Securely open using Authorization header
          await openWithAuth(documentUrl, targetValue.fileName || targetValue.documentType || fieldValue.target.name || 'document');
        }
      }
      // If we have a regular file upload
      else if (fieldValue && (fieldValue.path || fieldValue.id)) {
        // Construct file URL based on file path (preferred: query param)
        const documentUrl = fieldValue.path
          ? `${FILE_DOWNLOAD_ENDPOINT}?filePath=${encodeURIComponent(fieldValue.path)}`
          : `/v1/documents/${fieldValue.id}/download`;
        
        console.log('Document URL for file upload:', documentUrl);
        
        // Check if parent component has exposed handleDocumentPreview function
        if (window.handleDocumentPreview) {
          // Call the parent's document preview function with document info
          window.handleDocumentPreview({
            name: fieldValue.fileName || "uploaded file",
            documentUrl: documentUrl
          });
        } else {
          // Securely open using Authorization header
          await openWithAuth(documentUrl, fieldValue.fileName || 'uploaded file');
        }
      }
      // If we have a document in the nested structure format
      else if (fieldValue && fieldValue.target && fieldValue.target.name) {
        // Extract document info from the nested structure
        const documentType = fieldValue.target.name;
        const uid = fieldValue.target.uid || window.currentFormData?.employeeUid;
        
        // Construct document URL based on employee UID and document type
        const documentUrl = `${USER_DOCUMENTS_BASE}/${uid}/documents/${documentType}/download`;
        
        console.log('Document URL for nested structure:', documentUrl);
        
        // Check if parent component has exposed handleDocumentPreview function
        if (window.handleDocumentPreview) {
          // Call the parent's document preview function with document info
          window.handleDocumentPreview({
            name: fieldValue.target.value?.fileName || documentType || "document",
            documentUrl: documentUrl
          });
        } else {
          // Securely open using Authorization header
          await openWithAuth(documentUrl, fieldValue.target.value?.fileName || documentType || 'document');
        }
      }
    };
    
    // Helper to check non-empty references
    const hasNonEmptyRefs = (raw) => {
      const refs = parseDocumentReferences(raw);
      return Array.isArray(refs) && refs.length > 0;
    };

    // Consider a valid-looking documentUrl or non-empty references as evidence, even if hasFile=false
    const isLikelyValidUrl = (u) => {
      if (!u || typeof u !== 'string') return false;
      return u.startsWith('http') || u.startsWith('/v1/') || u.startsWith('/api/') || u.includes('/files/download');
    };
    const hasDocument = Boolean(
      (fieldValue && (fieldValue.path || fieldValue.id)) ||
      (fieldValue && fieldValue.hasFile === true && (fieldValue.uploadStatus === 'COMPLETED' || fieldValue.uploadStatus === 'UPLOADED')) ||
      (fieldValue && (
        isLikelyValidUrl(fieldValue.documentUrl) || hasNonEmptyRefs(fieldValue.documentReferences || fieldValue.documentReferenceList)
      )) ||
      (fieldValue && fieldValue.target && fieldValue.target.value && (
        isLikelyValidUrl(fieldValue.target.value.documentUrl) || hasNonEmptyRefs(fieldValue.target.value.documentReferences || fieldValue.target.value.documentReferenceList)
      ))
    );
    console.debug('[WorkflowFileField] hasDocument decision', {
      fieldName,
      hasDocument,
      path: fieldValue?.path,
      id: fieldValue?.id,
      uploadStatus: fieldValue?.uploadStatus,
      isReference: fieldValue?.isReference,
      documentUrl: fieldValue?.documentUrl,
      refsTop: fieldValue?.documentReferences || fieldValue?.documentReferenceList,
      nested: fieldValue?.target?.value || null
    });
    
    if (hasDocument) {
      // Support multi-document references from either top-level value or nested target.value
      const rawRefs = (fieldValue && (fieldValue.documentReferences || fieldValue.documentReferenceList))
        ? (fieldValue.documentReferences || fieldValue.documentReferenceList)
        : (fieldValue && fieldValue.target && fieldValue.target.value && (fieldValue.target.value.documentReferences || fieldValue.target.value.documentReferenceList))
          ? (fieldValue.target.value.documentReferences || fieldValue.target.value.documentReferenceList)
          : null;
      const refs = parseDocumentReferences(rawRefs);
      if (refs && refs.length > 0) {
        // Derive defaults for name fallback
        const baseName = fieldValue?.fileName || fieldValue?.documentType || fieldValue?.target?.name || 'document';

        // Helper to securely open a filePath with Authorization header
        const openFilePathInNewTab = async (filePath, name) => {
          try {
            // Clear any previous error before attempting
            setFileError(null);
            const fullUrl = `${FILE_DOWNLOAD_ENDPOINT}?filePath=${encodeURIComponent(filePath)}`;
            const axiosResp = await apiService.api.get(fullUrl, { responseType: 'blob' });
            const blob = axiosResp.data;
            const blobUrl = URL.createObjectURL(blob);
            // Open in same window
            window.location.href = blobUrl;
          } catch (e) {
            console.error('Error opening filePath document', e);
            if (!fileError) {
              setFileError('Unable to open file');
            }
          }
        };
        return (
          <Box sx={{ width: '100%' }}>
            <ul style={{ margin: 0, paddingLeft: 0, listStyleType: 'none' }}>
              {refs.map((ref, i) => {
                const name = ref.fileName || baseName || `document-${i + 1}`;
                const filePath = ref.filePath || ref.path || null;
                return (
                  <li key={`ref-${i}`} style={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
                      {ref.isPrimary && (
                        <StarIcon fontSize="small" sx={{ mr: 0.5, color: 'warning.main' }} />
                      )}
                      <InsertDriveFileIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography 
                        variant="body2" 
                        component="span"
                        noWrap
                        sx={{ 
                          maxWidth: 'calc(100% - 40px)', 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: '0.75rem',
                          color: filePath ? 'primary.main' : 'text.secondary',
                          cursor: filePath ? 'pointer' : 'default'
                        }}
                        onClick={() => {
                          if (filePath) {
                            openFilePathInNewTab(filePath, name);
                          }
                        }}
                        title={filePath ? name : 'File Not Found'}
                      >
                        {filePath ? name : 'File Not Found'}
                      </Typography>
                      {ref.isPrimary && (
                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>(Primary)</Typography>
                      )}
                    </Box>
                    <WDButton
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        if (filePath) {
                          openFilePathInNewTab(filePath, name);
                        } else {
                          // Fallback to generic preview handler
                          handlePreviewDocument();
                        }
                      }}
                      sx={{ ml: 1, textTransform: 'uppercase' }}
                    >
                      VIEW
                    </WDButton>
                  </li>
                );
              })}
            </ul>
          </Box>
        );
      }

      // Fallback: show single document behavior - match ticket FileField styling
      const fileName = 
        fieldValue.fileName || 
        (fieldValue.isReference ? fieldValue.documentType : null) ||
        (fieldValue.target?.value?.fileName) ||
        (fieldValue.target?.name) ||
        (fieldValue.documentType) ||
        "document";

      // Determine icon color based on extension (like ticket component)
      const fileExtension = String(fileName).split('.').pop().toLowerCase();
      let fileIconName = 'description';
      let fileIconColor = 'primary.main';
      if (['pdf'].includes(fileExtension)) {
        fileIconName = 'picture_as_pdf';
        fileIconColor = '#e53935';
      } else if (['jpg','jpeg','png','gif','bmp'].includes(fileExtension)) {
        fileIconName = 'image';
        fileIconColor = '#43a047';
      } else if (['doc','docx'].includes(fileExtension)) {
        fileIconName = 'description';
        fileIconColor = '#1565c0';
      } else if (['xls','xlsx'].includes(fileExtension)) {
        fileIconName = 'table_chart';
        fileIconColor = '#2e7d32';
      }

      // Simple display without strict actionable checks
      const displayName = fileName;

      return (
        <Box 
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px solid #ccc',
            borderRadius: 1,
            p: 1,
            bgcolor: '#f9f9f9',
            width: '100%'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            <Icon fontSize="small" sx={{ mr: 1, color: fileIconColor }}>{fileIconName}</Icon>
            <Typography 
              variant="body2" 
              sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={displayName}
            >
              {displayName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Icon 
              fontSize="small" 
              sx={{ cursor: 'pointer', mr: 1 }}
              onClick={handlePreviewDocument}
              title="View document"
            >
              visibility
            </Icon>
          </Box>
        </Box>
      );
    }

    // If no document and the field explicitly indicates it wasn't found in employee documents,
    // render a consistent informational message (parity with ticket FileField)
    if (fieldValue && fieldValue.notFoundInEmployeeDocs === true) {
      return (
        <Box 
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px dashed #bbb',
            borderRadius: 1,
            p: 1,
            bgcolor: '#fafafa',
            width: '100%'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
          <Icon fontSize="small" sx={{ mr: 1, color: '#9e9e9e' }}>do_not_disturb</Icon>
          <Typography variant="body2" color="text.secondary">
            File Not Found
          </Typography>
        </Box>
      </Box>
    );
  }

    // Determine accepted file types based on field configuration
    let acceptedType = "image/*";
    const fieldType = field.type?.toLowerCase() || "";
    
    if (fieldType === "pdf") {
      acceptedType = "application/pdf";
    } else if (fieldType === "document") {
      acceptedType =
        ".doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    }

    // Show file upload button
    return (
      <WDButton variant="text" component="label" style={{ fontSize: "10px" }}>
        {file ? file.name : `Select ${field.type || "file"}`}
        <input type="file" hidden onChange={onFileChange} accept={acceptedType} />
      </WDButton>
    );
  };

  return (
    <Box>
      <WDTypography
        variant="caption"
        sx={{ color: field.mandatory && !fieldValue ? "red" : null }}
      >
        {field.title || field.name || fieldName}&nbsp;{field.mandatory ? "*" : ""}
      </WDTypography>
      <WDBox
        display="flex"
        justifyContent="center"
        alignItems={fieldValue?.isReference && Array.isArray(fieldValue.documentReferences) && fieldValue.documentReferences.length > 0 ? "flex-start" : "center"}
        p={1}
        sx={{
          border: 1,
          borderColor: "grey.400",
          borderRadius: pxToRem(6),
          height: fieldValue?.isReference && Array.isArray(fieldValue.documentReferences) && fieldValue.documentReferences.length > 0 ? 'auto' : pxToRem(45),
        }}
      >
        {renderFileArea()}
        {renderActionButton((() => {
          // Recompute hasDocument here to keep behavior consistent with render
          const hasNonEmptyRefs = (raw) => {
            const refs = parseDocumentReferences(raw);
            return Array.isArray(refs) && refs.length > 0;
          };
          const isLikelyValidUrl2 = (u) => {
            if (!u || typeof u !== 'string') return false;
            return u.startsWith('http') || u.startsWith('/v1/') || u.startsWith('/api/') || u.includes('/files/download');
          };
          const hasDoc2 = Boolean(
            (fieldValue && (fieldValue.path || fieldValue.id)) ||
            (fieldValue && fieldValue.hasFile === true && (fieldValue.uploadStatus === 'COMPLETED' || fieldValue.uploadStatus === 'UPLOADED')) ||
            (fieldValue && (
              isLikelyValidUrl2(fieldValue.documentUrl) || hasNonEmptyRefs(fieldValue.documentReferences || fieldValue.documentReferenceList)
            )) ||
            (fieldValue && fieldValue.target && fieldValue.target.value && (
              isLikelyValidUrl2(fieldValue.target.value.documentUrl) || hasNonEmptyRefs(fieldValue.target.value.documentReferences || fieldValue.target.value.documentReferenceList)
            ))
          );
          console.debug('[WorkflowFileField] actionButton hasDocument decision', {
            fieldName,
            hasDoc2,
            value: fieldValue
          });
          return hasDoc2;
        })())}
      </WDBox>
      {fileError && (
        <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
          {fileError}
        </Typography>
      )}
    </Box>
  );
};

export default WorkflowFileField;
